import { Routes } from '@angular/router';
import config from '../../config/config';
import { Layouts } from './layout/data-acces/layout';
import { authGuard } from './core/guards/auth.guard';
import { OriginalIdGuard } from './core/guards/original-url-id.guard';
import { alumnoGuard } from './pages/escuela-isomaximus/data-access/guards/alumno.guard';

type DocRoute = {
  path: string;
  guard?: boolean;
};

// Lista de “documentos públicos” y si necesitan OriginalIdGuard
const publicDocs: DocRoute[] = [
  { path: '' },                       // login
  { path: '404' },
  { path: 'denegado' },
  { path: 'verificado' },
  { path: 'cuestionario' },
  { path: 'actas', guard: true },     // requiere OriginalIdGuard
  { path: 'cuestionario-IVAPTPANDO', guard: true },
  { path: 'encuesta' },
  { path: 'pruebas' },
  { path: 'firmaPruebaEc' },
  { path: 'terms' },
  { path: 'manual' },
  { path: 'recover-password' }
];

export const routes: Routes = [
  {
    path: config.URL_BASE_PATH,
    data: { layout: Layouts.Simple },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/home/home.component').then(
            (m) => m.HomeComponent
          ),
      },

      {
        path: 'login',
        loadComponent: () =>
          import('./pages/login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: '404',
        loadComponent: () =>
          import('./pages/failed/failed.component').then(
            (m) => m.FailedComponent
          ),
      },
      {
        path: 'denegado',
        loadComponent: () =>
          import('./pages/denied/denied.component').then(
            (m) => m.DeniedComponent
          ),
      },
      {
        path: 'verificado',
        loadComponent: () =>
          import('./pages/verificado/verificado.component').then(
            (m) => m.VerificadoComponent
          ),
      },
      {
        path: 'cuestionario',
        loadComponent: () =>
          import('./pages/cuestionario/cuestionario.component').then(
            (m) => m.CuestionarioComponent
          )
      },
      {
        path: 'actas',
        loadComponent: () =>
          import('./pages/actas/actas.component').then(
            (m) => m.ActasComponent
          )
      },
      {
        path: 'cuestionario-IVAPTPANDO',
        loadComponent: () =>
          import('./pages/cuestionario-ivapt-pando/cuestionario-ivapt-pando.component').then(
            (m) => m.CuestionarioIvaptPandoComponent
          )
      },
      {
        path: 'pruebas',
        loadComponent: () =>
          import('./testing/pruebas/pruebas.component').then(
            (m) => m.PruebasComponent
          )
      },
      {
        path: 'firmaPruebaEc',
        loadComponent: () =>
          import('./testing/firmaPruebaEc/firma-prueba-ec.component').then(
            (m) => m.FirmaPruebaEcComponent
          )
      },
      {
        path: 'terms',
        loadComponent: () =>
          import('./pages/terms/terms.component').then(
            (m) => m.TermsComponent
          )
      },
      {
        path: 'privacy-policy',
        loadComponent: () =>
          import('./pages/privacy-policy/privacy-policy.component').then(
            (m) => m.PrivacyPolicyComponent
          )
      },
      {
        path: 'manual',
        loadComponent: () =>
          import('./pages/manual/manual.component').then(
            (m) => m.ManualComponent
          )
      },
      {
        path: 'recover-password',
        loadComponent: () =>
          import('./pages/recuperar-contrasena/recuperar-contrasena.component').then(
            (m) => m.RecuperarContrasenaComponent
          )
      },
      {
        path: 'lista-verificacion-respuesta',
        loadComponent: () =>
          import('./pages/lista-verificacion/lista-verificacion.component').then(
            (m) => m.ListaVerificacionComponent
          )
      },
      {
        path: 'firma-documento/:tipo',
        loadComponent: () =>
          import('./pages/firma-generica/firma-generica.component').then(
            m => m.FirmaGenericaComponent
          ),
        canActivate: [OriginalIdGuard]
      },
      {
        // Entrada publica al motor de Firma Centralizada V2 para firmantes sin cuenta en el sistema
        // (ver src/app/pages/firma-externa). Piloto: FODA (apiSegment 'foda').
        path: 'firma-externa/:apiSegment/:tipoRegistro/:tipoSalida',
        loadComponent: () =>
          import('./pages/firma-externa/firma-externa.component').then(
            m => m.FirmaExternaComponent
          )
      },
      {
        path: 'mapa-procesos-pdf',
        loadComponent: () =>
          import('./pages/mapa-procesos-pdf/mapa-procesos-pdf.component').then(
            (m) => m.MapaProcesosPdfComponent
          )
      },
      {
        path: 'cursos/:nombre',
        loadComponent: () =>
          import('./pages/cursos-participantes/cursos-participantes.component').then(
            (m) => m.CursosParticipantesComponent
          )
      },
      {
        path: 'encuesta/:nombre',
        loadComponent: () =>
          import('./pages/encuesta-trabajadores/encuesta-trabajadores.component').then(
            (m) => m.EncuestaTrabajadoresComponent
          )
      },
      {
        path: 'evaluacion-proveedores',
        loadComponent: () =>
          import('./pages/evaluacion-proveedores/evaluacion-proveedores.component').then(
            (m) => m.EvaluacionProveedoresComponent
          )
      },
      {
        path: 'academy',
        loadComponent: () =>
          import('./pages/cursos-aula/cursos-aula.component').then(
            (m) => m.CursosAulaComponent
          )
      },
      {
        path: 'psicorisk-360',
        loadComponent: () =>
          import('./pages/psicorisk-360/psicorisk-360.component').then(
            (m) => m.Psicorisk360Component
          )
      },
      {
        path: 'resumen-psicorisk-360',
        loadComponent: () =>
          import('./pages/resumen-psicorisk-360/resumen-psicorisk-360.component').then(
            (m) => m.ResumenPsicorisk360Component
          )
      },
      {
        path: 'assist-autoevaluacion',
        loadComponent: () =>
          import('./pages/assist-autoevaluacion/assist-autoevaluacion.component').then(
            (m) => m.AssistAutoevaluacionComponent
          )
      },
      {
        // Formulario público (sin login) del Test de Madurez Digital SG-SST de ISOMaximus. El :id es
        // el int_encuesta_id de una encuesta en estado PUBLICADA (ver
        // src/app/iso-modulos/test-madurez-digital, botón "Ver respuestas" para copiar el enlace).
        path: 'test-madurez-digital/:id',
        loadComponent: () =>
          import('./pages/test-madurez-digital-publico/test-madurez-digital-publico.component').then(
            (m) => m.TestMadurezDigitalPublicoComponent
          )
      },
      {
        // Formulario público (sin login) del Diagnóstico Digital SG-SST de ISOMaximus. El :id es el
        // int_evaluacion_id de una Evaluación en estado PUBLICADA y dentro de su ventana de fechas (v2
        // -- ver API-ISO45001 -> docs/modules/diagnostico-digital/specs.md, CAMBIO DE REGLAS DE NEGOCIO).
        // El instrumento (25 preguntas fijas) sigue siendo el mismo para todas las Evaluaciones.
        path: 'diagnostico-digital/:id',
        loadComponent: () =>
          import('./pages/diagnostico-digital-publico/diagnostico-digital-publico.component').then(
            (m) => m.DiagnosticoDigitalPublicoComponent
          )
      },
      {
        // Escuela ISOMaximus: cursos de SST para personas externas al sistema, sin login de la plataforma
        // y con su propia sesión de alumno. Catálogo, landing, cuenta y aula se registran como hijos a
        // medida que existan. Backend: API-ISO45001 -> docs/modules/isomaximus-cursos/api.md. La
        // administración (staff) vive aparte, en src/app/iso-modulos/escuela.
        path: 'escuela',
        loadComponent: () =>
          import('./pages/escuela-isomaximus/escuela-isomaximus.component').then(
            (m) => m.EscuelaIsomaximusComponent
          ),
        children: [
          {
            // Catálogo: portada pública de la escuela.
            path: '',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/catalogo/catalogo.component').then((m) => m.CatalogoComponent)
          },
          {
            // Landing promocional de un curso publicado.
            path: 'curso/:slug',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/curso-landing/curso-landing.component').then(
                (m) => m.CursoLandingComponent
              )
          },
          {
            // Cuenta del alumno. "volver" (query param) indica a dónde regresar después de ingresar.
            path: 'ingresar',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/cuenta/ingresar/ingresar.component').then((m) => m.IngresarComponent)
          },
          {
            path: 'registro',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/cuenta/registro/registro.component').then((m) => m.RegistroComponent)
          },
          {
            path: 'verificar-correo',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/cuenta/verificar-correo/verificar-correo.component').then(
                (m) => m.VerificarCorreoComponent
              )
          },
          {
            path: 'recuperar-contrasena',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/cuenta/recuperar-contrasena/recuperar-contrasena.component').then(
                (m) => m.RecuperarContrasenaComponent
              )
          },
          {
            // Área del alumno: sin sesión, alumnoGuard lleva a ingresar y recuerda a dónde volver.
            path: 'mi-aprendizaje',
            canActivate: [alumnoGuard],
            loadComponent: () =>
              import('./pages/escuela-isomaximus/mi-aprendizaje/mi-aprendizaje.component').then(
                (m) => m.MiAprendizajeComponent
              )
          },
          {
            path: 'mi-perfil',
            canActivate: [alumnoGuard],
            loadComponent: () =>
              import('./pages/escuela-isomaximus/mi-perfil/mi-perfil.component').then((m) => m.MiPerfilComponent)
          },
          {
            // Verificación pública de certificados, sin sesión. Es el destino del QR impreso en el certificado
            // (CURSOS_URL_VERIFICACION_CERTIFICADO = {front}/escuela/verificar en el API).
            path: 'verificar',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/verificar/verificar-certificado.component').then(
                (m) => m.VerificarCertificadoComponent
              )
          },
          {
            path: 'verificar/:codigo',
            loadComponent: () =>
              import('./pages/escuela-isomaximus/verificar/verificar-certificado.component').then(
                (m) => m.VerificarCertificadoComponent
              )
          },
          {
            // Aula de un curso. La lección o evaluación abierta va en ?leccion= / ?evaluacion=.
            path: 'aula/:cursoId',
            canActivate: [alumnoGuard],
            loadComponent: () =>
              import('./pages/escuela-isomaximus/aula/aula.component').then((m) => m.AulaComponent)
          }
        ]
      }
    ],
  },
  {
    canMatch: [authGuard],
    path: config.URL_BASE_PATH,
    data: { layout: Layouts.Full },
    loadComponent: () =>
      import('./layout/full-layout/full-layout.component').then(
        (m) => m.FullLayoutComponent
      ),
    loadChildren: () =>
      import('./layout/full-layout/full-layout.routes').then((m) => m.routes),
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];
